import React, { useState, useEffect } from 'react';
import {
  Loader,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  Wrench,
  Server,
  ArrowRightLeft,
  Globe,
} from 'lucide-react';
import type { UnifiedToolPart, UnifiedToolState, ToolCategory } from '../../types/chat';

interface UnifiedToolCardProps {
  part: UnifiedToolPart;
  children?: React.ReactNode;
}

const stateConfig: Record<
  UnifiedToolState,
  {
    icon: typeof Loader;
    bgColor: string;
    borderColor: string;
    iconColor: string;
    labelColor: string;
    label: string;
    animate?: boolean;
  }
> = {
  pending: {
    icon: Loader,
    bgColor: 'bg-gray-50 dark:bg-gray-800/50',
    borderColor: 'border-gray-200 dark:border-gray-700',
    iconColor: 'text-gray-400',
    labelColor: 'text-gray-500 dark:text-gray-400',
    label: 'Preparing...',
    animate: true,
  },
  running: {
    icon: Loader,
    bgColor: 'bg-sky-50 dark:bg-sky-900/20',
    borderColor: 'border-sky-200 dark:border-sky-800',
    iconColor: 'text-sky-500',
    labelColor: 'text-sky-600 dark:text-sky-400',
    label: 'Running...',
    animate: true,
  },
  completed: {
    icon: CheckCircle,
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-800',
    iconColor: 'text-green-500',
    labelColor: 'text-green-600 dark:text-green-400',
    label: 'Completed',
  },
  failed: {
    icon: XCircle,
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    iconColor: 'text-red-500',
    labelColor: 'text-red-600 dark:text-red-400',
    label: 'Failed',
  },
};

const categoryBadge: Record<
  ToolCategory,
  { icon: typeof Server; color: string; prefix: string } | null
> = {
  mcp: { icon: Server, color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', prefix: 'MCP' },
  a2a: { icon: ArrowRightLeft, color: 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400', prefix: 'A2A' },
  browser: { icon: Globe, color: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400', prefix: 'Browser' },
  agentscope: null,
  unknown: null,
};

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function extractMCPText(result: unknown): string | null {
  if (!Array.isArray(result)) return null;
  const texts = result
    .filter(
      (item): item is { type: string; text: string } =>
        typeof item === 'object' &&
        item !== null &&
        item.type === 'text' &&
        typeof item.text === 'string',
    )
    .map((item) => item.text);
  return texts.length > 0 ? texts.join('\n') : null;
}

function extractMCPImages(result: unknown): string[] {
  if (!Array.isArray(result)) return [];
  return result
    .filter(
      (item): item is { type: string; data: string; mimeType?: string } =>
        typeof item === 'object' &&
        item !== null &&
        item.type === 'image' &&
        typeof item.data === 'string',
    )
    .map((item) => {
      const mime = item.mimeType || 'image/png';
      return `data:${mime};base64,${item.data}`;
    });
}

function isBrowserTool(toolName: string): boolean {
  return toolName.startsWith('browser_');
}

function getBrowserSummary(toolName: string, input?: Record<string, unknown>): string | null {
  if (!isBrowserTool(toolName)) return null;
  const action = toolName.replace(/^browser_/, '');

  switch (action) {
    case 'navigate':
      return input?.url ? `Navigate to ${input.url}` : 'Navigate';
    case 'navigate_back':
      return 'Go back';
    case 'click':
      return input?.element ? `Click: ${input.element}` : 'Click element';
    case 'type':
      if (input?.element && input?.text) return `Type "${truncate(String(input.text), 30)}" in: ${input.element}`;
      if (input?.text) return `Type "${truncate(String(input.text), 40)}"`;
      return 'Type text';
    case 'fill_form':
      return input?.fields ? `Fill ${(input.fields as unknown[]).length} fields` : 'Fill form';
    case 'select_option':
      return input?.element ? `Select in: ${input.element}` : 'Select option';
    case 'hover':
      return input?.element ? `Hover: ${input.element}` : 'Hover element';
    case 'drag':
      return input?.startElement && input?.endElement
        ? `Drag: ${input.startElement} → ${input.endElement}`
        : 'Drag element';
    case 'snapshot':
      return 'Capture page snapshot';
    case 'take_screenshot':
      return input?.fullPage ? 'Screenshot (full page)' : 'Screenshot';
    case 'press_key':
      return input?.key ? `Press: ${input.key}` : 'Press key';
    case 'evaluate':
      return 'Evaluate JavaScript';
    case 'console_messages':
      return 'Get console messages';
    case 'network_requests':
      return 'Get network requests';
    case 'close':
      return 'Close browser';
    case 'resize':
      return input?.width && input?.height ? `Resize to ${input.width}×${input.height}` : 'Resize browser';
    case 'tabs':
      return input?.action ? `Tabs: ${input.action}` : 'Manage tabs';
    case 'wait_for':
      if (input?.text) return `Wait for: "${truncate(String(input.text), 30)}"`;
      if (input?.textGone) return `Wait until gone: "${truncate(String(input.textGone), 30)}"`;
      if (input?.time) return `Wait ${input.time}s`;
      return 'Wait';
    case 'handle_dialog':
      return input?.accept ? 'Accept dialog' : 'Dismiss dialog';
    case 'file_upload':
      return 'Upload file';
    case 'install':
      return 'Install browser';
    case 'run_code':
      return 'Run Playwright code';
    default:
      return `Browser: ${action}`;
  }
}

function truncate(str: string, maxLen: number): string {
  return str.length > maxLen ? str.slice(0, maxLen) + '…' : str;
}

function LiveDuration({ startedAt }: { startedAt: number }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Date.now() - startedAt);
    }, 100);
    return () => clearInterval(interval);
  }, [startedAt]);

  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
      <Clock className="w-3 h-3" />
      {formatDuration(elapsed)}
    </span>
  );
}

function BrowserOutputDisplay({ output, toolName }: { output: unknown; toolName: string }) {
  const images = extractMCPImages(output);
  const mcpText = extractMCPText(output);
  const action = toolName.replace(/^browser_/, '');
  const isSnapshot = action === 'snapshot';

  return (
    <div className="space-y-2">
      {images.length > 0 && (
        <div className="space-y-2">
          {images.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`Browser screenshot ${i + 1}`}
              className="max-w-full h-auto rounded border border-gray-200 dark:border-gray-700"
              style={{ maxHeight: '400px' }}
            />
          ))}
        </div>
      )}
      {mcpText !== null && (
        <pre
          className={`text-xs overflow-x-auto whitespace-pre-wrap font-mono ${
            isSnapshot ? 'max-h-[300px] overflow-y-auto' : ''
          }`}
        >
          {mcpText}
        </pre>
      )}
      {images.length === 0 && mcpText === null && (
        <pre className="text-xs overflow-x-auto whitespace-pre-wrap font-mono">
          {typeof output === 'string' ? output : JSON.stringify(output, null, 2)}
        </pre>
      )}
    </div>
  );
}

export function UnifiedToolCard({ part, children }: UnifiedToolCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = stateConfig[part.state];
  const Icon = config.icon;
  const badge = categoryBadge[part.category];
  const browserSummary = getBrowserSummary(part.toolName, part.input);

  const hasInput = part.input && Object.keys(part.input).length > 0;
  const hasOutput = part.output !== undefined;
  const hasError = !!part.errorText;
  const hasDetails = hasInput || hasOutput || hasError;
  const isLoading = part.state === 'pending' || part.state === 'running';
  const isBrowser = isBrowserTool(part.toolName);

  const renderOutput = (output: unknown) => {
    if (isBrowser) {
      return <BrowserOutputDisplay output={output} toolName={part.toolName} />;
    }
    const mcpText = extractMCPText(output);
    if (mcpText !== null) {
      return (
        <pre className="text-xs overflow-x-auto whitespace-pre-wrap font-mono">
          {mcpText}
        </pre>
      );
    }
    if (typeof output === 'string') {
      return (
        <pre className="text-xs overflow-x-auto whitespace-pre-wrap font-mono">
          {output}
        </pre>
      );
    }
    return (
      <pre className="text-xs overflow-x-auto whitespace-pre-wrap font-mono">
        {JSON.stringify(output, null, 2)}
      </pre>
    );
  };

  return (
    <div className="my-1">
      <div
        className={`rounded-lg border overflow-hidden transition-all duration-200 ${config.bgColor} ${config.borderColor}`}
        data-testid={`unified-tool-${part.toolCallId}`}
        data-tool-name={part.toolName}
        data-state={part.state}
        data-category={part.category}
      >
        <button
          type="button"
          className={`flex items-center gap-2 px-3 py-2 w-full text-left bg-transparent border-0 ${
            hasDetails ? 'cursor-pointer hover:bg-black/5 dark:hover:bg-white/5' : ''
          }`}
          onClick={hasDetails ? () => setIsExpanded(!isExpanded) : undefined}
          aria-expanded={hasDetails ? isExpanded : undefined}
          disabled={!hasDetails}
        >
          <div className={`flex-shrink-0 ${config.iconColor}`}>
            {config.animate ? (
              <Icon className="w-4 h-4 animate-spin" />
            ) : (
              <Icon className="w-4 h-4" />
            )}
          </div>

          <Wrench className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          <span className="font-medium text-sm text-gray-700 dark:text-gray-200 truncate max-w-[200px]" title={part.toolName}>
            {part.toolName}
          </span>

          {badge && (
            <span
              className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${badge.color}`}
            >
              <badge.icon className="w-3 h-3" />
              {badge.prefix}
              {part.serverName ? `:${part.serverName}` : ''}
            </span>
          )}

          {browserSummary ? (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[250px]" title={browserSummary}>
              {browserSummary}
            </span>
          ) : (
            <span className={`text-xs font-medium ${config.labelColor}`}>
              {config.label}
            </span>
          )}

          {part.durationMs != null && !isLoading ? (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 ml-auto">
              <Clock className="w-3 h-3" />
              {formatDuration(part.durationMs)}
            </span>
          ) : isLoading && part.startedAt ? (
            <span className="ml-auto">
              <LiveDuration startedAt={part.startedAt} />
            </span>
          ) : null}

          {hasDetails && (
            <span className={`text-gray-400 ${part.durationMs == null && !isLoading ? 'ml-auto' : ''}`}>
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </span>
          )}
        </button>

        {isExpanded && hasDetails && (
          <div className="px-3 pb-3 pt-1 border-t border-gray-200/50 dark:border-gray-700/50 space-y-3 bg-white/50 dark:bg-gray-900/50">
            {hasInput && (
              <div>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Input</div>
                <pre className="text-xs bg-white dark:bg-gray-900 dark:text-gray-300 rounded-md p-2 overflow-x-auto max-h-[200px] overflow-y-auto border border-gray-100 dark:border-gray-700 font-mono">
                  {JSON.stringify(part.input, null, 2)}
                </pre>
              </div>
            )}
            {hasOutput && part.state === 'completed' && (
              <div>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Output</div>
                <div className="bg-white dark:bg-gray-900 rounded-md p-2 border border-gray-100 dark:border-gray-700 max-h-[200px] overflow-y-auto dark:text-gray-300">
                  {renderOutput(part.output)}
                </div>
              </div>
            )}
            {hasError && (
              <div>
                <div className="text-xs font-medium text-red-500 dark:text-red-400 mb-1">Error</div>
                <pre className="text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md p-2 overflow-x-auto border border-red-100 dark:border-red-800 font-mono">
                  {part.errorText}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>

      {children && (
        <div className="ml-6 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
          {children}
        </div>
      )}
    </div>
  );
}
